import React, { useEffect, useState, useMemo } from "react";
import { X, Play, ChevronLeft, ChevronRight } from "lucide-react";
import BrandText from "./BrandText";

const RATIO_CLASS = {
    square: "aspect-square",
    wide: "aspect-[4/3]",
    portrait: "aspect-[3/4]",
};

function MediaTile({ item, onClick, idPrefix = "media" }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`group relative overflow-hidden bg-[var(--crewz-surface)] ${
                RATIO_CLASS[item.ratio] || "aspect-square"
            } w-full text-left ${item.featured ? "md:col-span-2 md:row-span-2" : ""}`}
            data-testid={`${idPrefix}-tile-${item.type}${item.featured ? "-featured" : ""}`}
        >
            {item.type === "image" ? (
                <img
                    src={item.src}
                    alt={item.caption || "Moment"}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-95 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-700"
                />
            ) : (
                <>
                    {item.thumbnail ? (
                        <img
                            src={item.thumbnail}
                            alt={item.caption || "Video"}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-700"
                        />
                    ) : (
                        <video
                            src={item.src}
                            muted
                            loop
                            autoPlay={item.featured}
                            playsInline
                            preload="metadata"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    )}
                    <div
                        className={`absolute inset-0 flex items-center justify-center ${
                            item.featured ? "bg-black/15" : "bg-black/20"
                        } group-hover:bg-black/10 transition-colors`}
                    >
                        <span
                            className={`inline-flex items-center justify-center ${
                                item.featured ? "w-20 h-20" : "w-14 h-14"
                            } rounded-full bg-[var(--crewz-gold)] text-[var(--crewz-bg)] group-hover:scale-110 transition-transform shadow-2xl`}
                        >
                            <Play size={item.featured ? 28 : 20} fill="currentColor" className="ml-1" />
                        </span>
                    </div>
                </>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--crewz-bg)]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            {item.caption && (
                <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-[var(--crewz-gold-soft)] font-medium">
                        {item.type === "video" ? "Video" : "Photo"}
                    </p>
                    <p className="text-sm text-white font-body mt-1">{item.captionNode || <BrandText>{item.caption}</BrandText>}</p>
                </div>
            )}
        </button>
    );
}

function Lightbox({ items, index, onClose, onPrev, onNext, idPrefix = "media" }) {
    const item = items[index];

    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") onPrev();
            if (e.key === "ArrowRight") onNext();
        };
        window.addEventListener("keydown", handler);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", handler);
            document.body.style.overflow = "";
        };
    }, [onClose, onPrev, onNext]);

    if (!item) return null;

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center"
            onClick={onClose}
            data-testid={`${idPrefix}-lightbox`}
        >
            <button
                onClick={onClose}
                className="absolute top-6 right-6 text-white/70 hover:text-white p-2 z-10"
                aria-label="Close"
                data-testid="lightbox-close"
            >
                <X size={28} />
            </button>

            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onPrev();
                }}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/60 hover:text-[var(--crewz-gold)] p-2 z-10"
                aria-label="Previous"
                data-testid="lightbox-prev"
            >
                <ChevronLeft size={36} />
            </button>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onNext();
                }}
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/60 hover:text-[var(--crewz-gold)] p-2 z-10"
                aria-label="Next"
                data-testid="lightbox-next"
            >
                <ChevronRight size={36} />
            </button>

            <div
                className="relative max-w-[92vw] max-h-[88vh] flex flex-col items-center"
                onClick={(e) => e.stopPropagation()}
            >
                {item.type === "image" ? (
                    <img
                        src={item.src}
                        alt={item.caption || ""}
                        className="max-w-full max-h-[80vh] object-contain"
                    />
                ) : (
                    <video
                        src={item.src}
                        controls
                        autoPlay
                        playsInline
                        className="max-w-full max-h-[80vh]"
                    />
                )}
                <div className="mt-4 text-center">
                    {item.caption && (
                        <p className="font-body text-sm text-white/90">{item.captionNode || <BrandText>{item.caption}</BrandText>}</p>
                    )}
                    <p className="text-[10px] tracking-[0.32em] uppercase text-white/40 font-medium mt-2">
                        {index + 1} / {items.length}
                    </p>
                </div>
            </div>
        </div>
    );
}

export default function MediaGallery({ items, idPrefix = "media", showFilter = true }) {
    const [filter, setFilter] = useState("all");
    const [activeIndex, setActiveIndex] = useState(null);

    const filtered = useMemo(
        () => (filter === "all" ? items : items.filter((m) => m.type === filter)),
        [filter, items]
    );

    const counts = useMemo(
        () => ({
            all: items.length,
            image: items.filter((m) => m.type === "image").length,
            video: items.filter((m) => m.type === "video").length,
        }),
        [items]
    );

    const open = (i) => setActiveIndex(i);
    const close = () => setActiveIndex(null);
    const prev = () =>
        setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
    const next = () => setActiveIndex((i) => (i + 1) % filtered.length);

    return (
        <>
            {showFilter && (
                <div className="flex items-center gap-2 mb-8" data-testid={`${idPrefix}-filters`}>
                    {[
                        { v: "all", l: "All" },
                        { v: "image", l: "Photos" },
                        { v: "video", l: "Videos" },
                    ].map((f) => (
                        <button
                            key={f.v}
                            onClick={() => setFilter(f.v)}
                            className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-medium border transition-all duration-300 ${
                                filter === f.v
                                    ? "border-[var(--crewz-gold)] text-[var(--crewz-bg)] bg-[var(--crewz-gold)]"
                                    : "border-white/15 text-[var(--crewz-text)] hover:border-[var(--crewz-gold)] hover:text-[var(--crewz-gold)]"
                            }`}
                            data-testid={`${idPrefix}-filter-${f.v}`}
                        >
                            {f.l} <span className="opacity-60 ml-1">{counts[f.v]}</span>
                        </button>
                    ))}
                </div>
            )}

            {filtered.length === 0 ? (
                <div className="border border-white/10 bg-[var(--crewz-surface)] py-20 text-center">
                    <p className="overline mb-3">Nothing here yet</p>
                    <p className="font-body text-zinc-400">No {filter}s uploaded yet.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 auto-rows-fr">
                    {filtered.map((item, i) => (
                        <MediaTile
                            key={i}
                            item={item}
                            onClick={() => open(i)}
                            idPrefix={idPrefix}
                        />
                    ))}
                </div>
            )}

            {activeIndex !== null && (
                <Lightbox
                    items={filtered}
                    index={activeIndex}
                    onClose={close}
                    onPrev={prev}
                    onNext={next}
                    idPrefix={idPrefix}
                />
            )}
        </>
    );
}
